import { create } from 'zustand';
import type { OsmGroupOverride } from '../data/overridesRuntime';
import { fetchAndApplyOverrides } from '../data/refreshOverrides';

/**
 * osmGroupOverridesDraft — guarda directamente una reasignación de cableado
 * OSM→grupo (sección "Cablear grupos" de Modo Edición). Guardado real desde
 * 2026-09-10 (antes: export a un snippet que nunca se pegaba). Sin estado
 * propio — tras cada escritura se vuelve a pedir `GET /api/overrides` y se
 * aplica sobre `overridesRuntime.ts`.
 */
export type { OsmGroupOverride as OsmGroupOverrideDraft } from '../data/overridesRuntime';

interface DraftState {
  saving: boolean;
  stage: (d: OsmGroupOverride) => Promise<boolean>;
  remove: (osmId: number) => Promise<boolean>;
  exclude: (osmId: number) => Promise<boolean>;
}

export const useOsmGroupOverridesDraft = create<DraftState>((set) => ({
  saving: false,
  stage: async (d) => {
    set({ saving: true });
    try {
      const res = await fetch(`/api/overrides/group_wiring/${d.osmId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(d),
      });
      if (!res.ok) return false;
      await fetchAndApplyOverrides();
      return true;
    } catch {
      return false;
    } finally {
      set({ saving: false });
    }
  },
  remove: async (osmId) => {
    set({ saving: true });
    try {
      const res = await fetch(`/api/overrides/group_wiring/${osmId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok && res.status !== 404) return false;
      await fetchAndApplyOverrides();
      return true;
    } catch {
      return false;
    } finally {
      set({ saving: false });
    }
  },
  // "Este semáforo no es de este grupo" (2026-09-15) — a diferencia de
  // `remove` (que solo borra un override manual puntual), esto excluye la
  // asignación sea cual sea su origen, automático o manual — ver
  // group_wiring_excluded / applyGroupWiringExclusions.ts.
  exclude: async (osmId) => {
    set({ saving: true });
    try {
      const res = await fetch(`/api/overrides/group_wiring_excluded/${osmId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) return false;
      await fetchAndApplyOverrides();
      return true;
    } catch {
      return false;
    } finally {
      set({ saving: false });
    }
  },
}));
